import { matchPath } from 'react-router-dom';
import type { NavGroup, NavItem, NavTab } from './navigation';

const AQUARIUM_PARAM = ':aquariumId';

/** True when the pathname is one of the tab's pages (its path, sub-routes unless `end`, `alsoMatches`). */
export function tabMatches(tab: NavTab, pathname: string): boolean {
  const patterns = [
    { path: tab.path, end: tab.end ?? false },
    ...(tab.alsoMatches ?? []).map((path) => ({ path, end: false })),
  ];
  return patterns.some((pattern) => matchPath(pattern, pathname) !== null);
}

/** The aquarium id in the URL (/aquariums/:aquariumId/…), or null. */
export function aquariumIdFromPath(pathname: string): string | null {
  const match = matchPath({ path: `/aquariums/${AQUARIUM_PARAM}/*`, end: false }, pathname);
  const id = match?.params.aquariumId;
  return id && id !== 'new' ? id : null;
}

/** The tab's URL; null when it needs an aquarium and none is known. */
export function tabHref(tab: NavTab, aquariumId: string | null): string | null {
  if (!tab.path.includes(AQUARIUM_PARAM)) return tab.path;
  return aquariumId === null ? null : tab.path.replace(AQUARIUM_PARAM, encodeURIComponent(aquariumId));
}

export interface TabContext {
  aquariumId: string | null;
  usesZeovit: boolean;
}

/** The tabs the user can open now (aquarium known, ZeoVit gate). */
export function visibleTabs(item: NavItem, ctx: TabContext): NavTab[] {
  return (item.tabs ?? []).filter(
    (tab) => tabHref(tab, ctx.aquariumId) !== null && (!tab.requiresZeovitSupplySystem || ctx.usesZeovit),
  );
}

/** The area (sidebar item with tabs) the pathname belongs to, with its matching tab. */
export function findActiveArea(
  groups: readonly NavGroup[],
  pathname: string,
): { item: NavItem; tab: NavTab } | null {
  for (const group of groups) {
    for (const item of group.items) {
      const tab = item.tabs?.find((t) => tabMatches(t, pathname));
      if (tab) return { item, tab };
    }
  }
  return null;
}
